import rawData from '@/data/djusd.json';
import type { School, StudyArea } from './types';

export const schools: School[] = rawData.schools as School[];
export const areas: StudyArea[] = rawData.areas as StudyArea[];

export const schoolNames = schools.map((s) => s.name);

export function getSchoolColor(schoolName: string): string {
  const school = schools.find((s) => s.name === schoolName);
  if (!school) return 'rgb(128,128,128)';
  return `rgb(${school.color.r},${school.color.g},${school.color.b})`;
}

export function getSchoolColorHex(schoolName: string): string {
  const school = schools.find((s) => s.name === schoolName);
  if (!school) return '#808080';
  const { r, g, b } = school.color;
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * When a school is turned off, reassign its areas to the nearest open school.
 * Mirrors the VBA SchoolClick logic.
 */
export function computeAssignments(
  schoolStatus: Record<string, boolean>,
): Record<string, string> {
  const assignments: Record<string, string> = {};

  for (const area of areas) {
    const currentZone = area.zone;

    // If the area's zone school is open, keep it assigned there
    if (schoolStatus[currentZone]) {
      assignments[area.id] = currentZone;
      continue;
    }

    // School is closed - find nearest open school
    const nearest = findNearestOpenSchool(area, schoolStatus);
    assignments[area.id] = nearest;
  }

  return assignments;
}

/**
 * Assign all areas to their minimum distance school (regardless of zone).
 * Mirrors VBA MinimumDistanceButton.
 */
export function computeMinDistanceAssignments(
  schoolStatus: Record<string, boolean>,
): Record<string, string> {
  const assignments: Record<string, string> = {};

  for (const area of areas) {
    if (schoolStatus[area.minimumDistanceSchool]) {
      assignments[area.id] = area.minimumDistanceSchool;
    } else {
      assignments[area.id] = findNearestOpenSchool(area, schoolStatus);
    }
  }

  return assignments;
}

/**
 * Reset all areas to their original zone assignment.
 * Mirrors VBA currentboundaries.
 */
export function computeCurrentBoundaryAssignments(): Record<string, string> {
  const assignments: Record<string, string> = {};
  for (const area of areas) {
    assignments[area.id] = area.zone;
  }
  return assignments;
}

function findNearestOpenSchool(
  area: StudyArea,
  schoolStatus: Record<string, boolean>,
): string {
  let minDist = Infinity;
  let nearest = '';

  for (const [schoolName, isOpen] of Object.entries(schoolStatus)) {
    if (!isOpen) continue;
    const dist = area.distances[schoolName] ?? Infinity;
    if (dist < minDist) {
      minDist = dist;
      nearest = schoolName;
    }
  }

  return nearest || area.zone;
}

/**
 * Get distance from an area to its assigned school.
 */
export function getDistanceToAssigned(
  area: StudyArea,
  assignedSchool: string,
): number {
  return area.distances[assignedSchool] ?? 0;
}

/**
 * Compute heatmap color based on distance (0-3 miles).
 * Uses sqrt scale to amplify differences in the 0-1.5mi range where most data lives.
 * Multi-stop gradient: bright green -> yellow -> orange -> deep red.
 */
export function getHeatmapColor(distance: number): string {
  const clamped = Math.max(0, Math.min(3, distance));
  // sqrt scale: spreads the dense 0-1mi range across more of the color spectrum
  const t = Math.sqrt(clamped / 3);

  // Multi-stop gradient for maximum contrast
  // 0.0 = bright green, 0.35 = yellow, 0.65 = orange, 1.0 = deep red
  const stops = [
    { t: 0.0, r: 0, g: 200, b: 60 },    // bright green
    { t: 0.35, r: 255, g: 220, b: 0 },   // yellow
    { t: 0.65, r: 255, g: 100, b: 0 },   // orange
    { t: 1.0, r: 210, g: 0, b: 0 },      // deep red
  ];

  // Find the two stops we're between
  let low = stops[0];
  let high = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i++) {
    if (t >= stops[i].t && t <= stops[i + 1].t) {
      low = stops[i];
      high = stops[i + 1];
      break;
    }
  }

  const segT = high.t === low.t ? 0 : (t - low.t) / (high.t - low.t);
  const r = Math.round(low.r + (high.r - low.r) * segT);
  const g = Math.round(low.g + (high.g - low.g) * segT);
  const b = Math.round(low.b + (high.b - low.b) * segT);

  return `rgb(${r},${g},${b})`;
}

/**
 * Compute enrollment totals per school given current assignments and selected year.
 */
export function computeEnrollmentBySchool(
  assignments: Record<string, string>,
  year: string,
): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const school of schools) {
    totals[school.name] = 0;
  }

  for (const area of areas) {
    const school = assignments[area.id];
    if (school && totals[school] !== undefined) {
      totals[school] += area.population[year] ?? 0;
    }
  }

  // Round results
  for (const key of Object.keys(totals)) {
    totals[key] = Math.round(totals[key]);
  }

  return totals;
}

/**
 * Compute average distance for students at each school.
 */
export function computeAvgDistanceBySchool(
  assignments: Record<string, string>,
  year: string,
): Record<string, number> {
  const totalWeightedDist: Record<string, number> = {};
  const totalPop: Record<string, number> = {};

  for (const school of schools) {
    totalWeightedDist[school.name] = 0;
    totalPop[school.name] = 0;
  }

  for (const area of areas) {
    const school = assignments[area.id];
    if (!school || totalPop[school] === undefined) continue;
    const pop = area.population[year] ?? 0;
    const dist = area.distances[school] ?? 0;
    totalWeightedDist[school] += pop * dist;
    totalPop[school] += pop;
  }

  const avg: Record<string, number> = {};
  for (const school of schools) {
    avg[school.name] =
      totalPop[school.name] > 0
        ? totalWeightedDist[school.name] / totalPop[school.name]
        : 0;
  }

  return avg;
}
